import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFirebase } from '../../context/FirebaseContext';
import { postFeedback } from '../../api/feedback';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose }) => {
    const { auth } = useFirebase();
    const [rating, setRating] = useState(0);
    const [type, setType] = useState('GENERAL');
    const [content, setContent] = useState('');
    const [includeMetadata, setIncludeMetadata] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            // Reset state when opening
            setRating(0);
            setType('GENERAL');
            setContent('');
            setError(null);
            setIncludeMetadata(true);
            setIsSuccess(false);
        }
    }, [isOpen]);

    useEffect(() => {
        let timeout;
        if (isSuccess) {
            timeout = setTimeout(() => {
                onClose();
            }, 2000);
        }
        return () => clearTimeout(timeout);
    }, [isSuccess, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (rating === 0) {
            setError('Please select a rating');
            return;
        }

        if (content.length > 1000) {
            setError('Comment is too long (max 1000 characters)');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const token = await auth.currentUser.getIdToken();

            let metadata = null;
            if (includeMetadata) {
                metadata = {
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString(),
                    screenSize: `${window.innerWidth}x${window.innerHeight}`
                };
            }

            const feedbackData = {
                type,
                rating,
                content,
                metadata
            };

            const response = await postFeedback(token, feedbackData);

            if (response.error) {
                throw new Error(response.error);
            }

            // Success
            setIsSuccess(true);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to submit feedback');
            setIsSubmitting(false);
        }
    };

    const handleRating = (r) => {
        setRating(r);
        if (error === 'Please select a rating') setError(null);
    };

    return createPortal(
        <div className="feedback-modal-overlay" onClick={onClose}>
            <div className="feedback-modal-container" onClick={e => e.stopPropagation()}>
                <div className="feedback-modal-header">
                    <h2 className="feedback-modal-title">
                        {isSuccess ? 'Thank You!' : 'Send Feedback'}
                    </h2>
                    <button className="feedback-modal-close" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="feedback-modal-content">
                    {isSuccess ? (
                        <div className="feedback-success-view">
                            <CheckCircleOutlineIcon className="feedback-success-icon" />
                            <p className="feedback-success-message">
                                Your feedback has been sent successfully.
                            </p>
                            <p className="feedback-success-sub">
                                We appreciate your input!
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="feedback-rating-section">
                                <span className="feedback-label">How was your experience?</span>
                                <div className="feedback-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            className={`star-btn ${rating >= star ? 'active' : ''}`}
                                            onClick={() => handleRating(star)}
                                            type="button"
                                        >
                                            ★
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="feedback-type-section">
                                <label className="feedback-label">Feedback Type</label>
                                <select
                                    className="feedback-type-select"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <option value="GENERAL">General Feedback</option>
                                    <option value="BUG">Bug Report</option>
                                    <option value="FEATURE">Feature Request</option>
                                </select>
                            </div>

                            <div className="feedback-comment-section">
                                <label className="feedback-label">Tell us more</label>
                                <textarea
                                    className="feedback-textarea"
                                    placeholder="What did you like? What can we improve?"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    maxLength={1000}
                                />
                                <div className={`character-count ${content.length >= 900 ? 'limit-near' : ''}`}>
                                    {content.length} / 1000
                                </div>
                            </div>

                            <label className="metadata-check">
                                <input
                                    type="checkbox"
                                    className="metadata-checkbox"
                                    checked={includeMetadata}
                                    onChange={(e) => setIncludeMetadata(e.target.checked)}
                                />
                                <span className="metadata-label">Include system info (Browser, URL)</span>
                            </label>

                            {error && <div className="error-message" style={{ color: 'var(--error-color)', fontSize: '0.9rem' }}>{error}</div>}
                        </>
                    )}
                </div>

                {!isSuccess && (
                    <div className="feedback-modal-footer">
                        <button
                            className="btn-cancel"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-submit"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? <span className="spinner"></span> : 'Submit Feedback'}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default FeedbackModal;
